import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, AlertDescription, Button } from '@/components/ui'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import type { Conversation } from './conversation'
import { Composer, type ComposerHandle } from './Composer'
import { usePanelControls } from './helpFeedbackContext'
import { PlainReport } from './PlainReport'
import { Receipt } from './Receipt'
import { ResumeOffer } from './ResumeOffer'
import { Turns, type TurnHandlers } from './Turns'
import { useAsk } from './useAsk'
import { DISCARDED, TRY_AGAIN, VIEW_AS_LINE, receiptLine, sendFailedLine } from './helpFeedbackText'

/** What the one polite live region reads: the newest thing the panel said, once it has settled. */
function announcement(c: Conversation): string {
  if (c.sent) return receiptLine(c.sent.reference)
  if (c.sendError) return sendFailedLine(c.sendError)
  const last = c.turns[c.turns.length - 1]
  if (!last) return ''
  switch (last.type) {
    case 'assistant':
      return [last.text, last.question].filter(Boolean).join(' ')
    case 'answer':
      return last.streaming ? '' : last.markdown
    case 'card':
      return last.lead
    case 'notFound':
      return last.text
    default:
      return ''
  }
}

/** Ask (SA-1310 spec §4 to §9): the conversation, its card and the composer under it. */
export function AskTab() {
  const c = useHelpFeedbackStore((s) => s.conversation)
  const status = useHelpFeedbackStore((s) => s.status)
  const offerResume = useHelpFeedbackStore((s) => s.offerResume)
  const dispatch = useHelpFeedbackStore((s) => s.dispatch)
  const { startFresh } = usePanelControls()
  const ask = useAsk()
  const composer = useRef<ComposerHandle>(null)
  const scroller = useRef<HTMLDivElement>(null)
  // The resume line's "ago" is read when the offer shows, not on every render.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (offerResume) setNow(Date.now())
  }, [offerResume])

  const { chip, resolved, notQuite, send, sendQuestion, askAnotherWay } = ask
  const handlers: TurnHandlers = useMemo(() => ({
    chip,
    resolved,
    notQuite,
    editCard: (patch) => dispatch({ type: 'cardEdit', patch }),
    removeChip: (id) => dispatch({ type: 'removeChip', chip: id }),
    send: () => void send(),
    addMore: () => {
      dispatch({ type: 'addMore' })
      composer.current?.focus()
    },
    sendQuestion: () => void sendQuestion(),
    askAnotherWay: () => {
      askAnotherWay()
      composer.current?.focus()
    },
  }), [chip, resolved, notQuite, send, sendQuestion, askAnotherWay, dispatch])

  const last = c?.turns[c.turns.length - 1]
  const tail = last?.type === 'answer' ? last.markdown.length : 0
  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [c?.turns.length, tail, c?.sent, c?.sendError, offerResume])

  if (!c) return null

  if (status === 'unavailable') {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {ask.viewAs && <p className="mb-3 text-sm text-muted-foreground">{VIEW_AS_LINE}</p>}
        <PlainReport c={c} viewAs={ask.viewAs} />
      </div>
    )
  }

  const discard = () => {
    startFresh()
    const store = useHelpFeedbackStore.getState()
    store.setDraft('')
    store.dispatch({ type: 'say', text: DISCARDED })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={scroller} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {offerResume ? (
          <ResumeOffer
            c={c}
            now={now}
            disabled={ask.viewAs}
            onKeep={() => useHelpFeedbackStore.getState().setOfferResume(false)}
            onSend={() => {
              useHelpFeedbackStore.getState().setOfferResume(false)
              void send()
            }}
            onDiscard={discard}
          />
        ) : (
          <Turns c={c} viewAs={ask.viewAs} handlers={handlers} />
        )}
        {c.sendError && !c.sent && (
          <Alert variant="destructive">
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>{sendFailedLine(c.sendError)}</span>
              <Button size="sm" variant="outline" onClick={ask.tryAgain} disabled={ask.viewAs}>{TRY_AGAIN}</Button>
            </AlertDescription>
          </Alert>
        )}
        {c.sent && <Receipt reference={c.sent.reference} />}
      </div>
      <p className="sr-only" aria-live="polite" data-testid="help-feedback-live">{announcement(c)}</p>
      {!c.sent && !offerResume && (
        <div className="border-t border-border px-5 py-3">
          {ask.viewAs && <p className="mb-2 text-xs text-muted-foreground">{VIEW_AS_LINE}</p>}
          <Composer
            ref={composer}
            addingMore={c.addingMore}
            disabled={ask.viewAs}
            busy={c.waiting === 'turn'}
            onSubmit={ask.say}
          />
        </div>
      )}
    </div>
  )
}
